import { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapUnderline from '@tiptap/extension-underline';
import { Bold, Italic, Underline, Image as ImageIcon } from 'lucide-react';
import { createLinkExtension, linkEditorProps } from '../link-extension';
import { createImageExtension } from '../image-resize-extension';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { preventEnterSubmit, cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { VariableSelector } from './VariableSelector';
import { TagInput } from './TagInput';
import { SendTestEmailDialog } from '../SendTestEmailDialog';
import { LinkPopover } from '../LinkPopover';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { uploadEmailImage, ImageUploadError } from '@/lib/email/image-upload';
import { validateTemplate, extractVariables } from '@/lib/email/template-utils';
import type { EmailTemplate, TemplateVariable } from '@/lib/email/template-utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import DOMPurify from 'dompurify';
import { renderCampaignTemplate, KNOWN_VARIABLE_NAMES } from '@/lib/email/campaign-merge';
import type { CampaignMergeRecipient } from '@/lib/email/campaign-merge';

// Hardcoded sample merge data for the Preview tab. Covers every name in
// KNOWN_VARIABLE_NAMES (renderCampaignTemplate aliases firstname→first_name,
// lastname→last_name, companyname→company), so no KNOWN field renders empty in
// Preview. Unmapped {{tokens}} are still left literal by renderCampaignTemplate.
const PREVIEW_SAMPLE_RECIPIENT: CampaignMergeRecipient = {
  first_name: 'Jane',
  last_name: 'Doe',
  company: 'Acme Co.',
  email: 'jane@example.com',
  occupation: 'Director',
  phone: '(555) 010-1234',
};

// Local copy of CampaignWizardDialog.fillBlankLines (kept out of a shared helper
// per the locked scope decision). Must stay equivalent to the wizard's version.
// Blank <p></p>/<p><br></p> collapse to zero height in real clients; fill with
// &nbsp; so a blank line keeps one line of height in the preview.
export function fillBlankLines(html: string): string {
  return html
    .replace(/<p>\s*<\/p>/g, '<p>&nbsp;</p>')
    .replace(/<p>\s*<br\s*\/?>\s*<\/p>/g, '<p>&nbsp;</p>');
}

// Like renderCampaignTemplate, but wraps each substituted value in a <mark> so the
// Preview visually distinguishes merged sample data ("Jane" came from {{first_name}})
// from literal template text. Mirrors campaign-merge.ts's variableMap + gi regex.
// DOMPurify's default config keeps <mark> + class, so the highlight survives sanitize.
// Unmapped tokens are not in variableMap and are left literally intact (matching
// what recipients receive).
export function renderHighlightedTemplate(
  template: string,
  recipient: CampaignMergeRecipient,
): string {
  const variableMap: Record<string, string> = {
    first_name: recipient.first_name || '',
    firstname: recipient.first_name || '',
    last_name: recipient.last_name || '',
    lastname: recipient.last_name || '',
    company: recipient.company || '',
    companyname: recipient.company || '',
    email: recipient.email || '',
    occupation: recipient.occupation || '',
    phone: recipient.phone || '',
  };
  let rendered = template;
  for (const [varName, value] of Object.entries(variableMap)) {
    const regex = new RegExp(`\\{\\{\\s*${varName}\\s*\\}\\}`, 'gi');
    rendered = rendered.replace(
      regex,
      `<mark class="bg-muted text-foreground rounded px-0.5">${value}</mark>`,
    );
  }
  return rendered;
}

// Well-formed {{token}} names in the source that are NOT known merge fields, so
// Preview can surface a soft "will send as-is" warning. Original casing preserved
// for display; membership test is lowercased to match KNOWN_VARIABLE_NAMES.
export function findUnmappedTokens(html: string): string[] {
  const found = new Set<string>();
  for (const m of html.matchAll(/\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g)) {
    const name = m[1].toLowerCase();
    if (!KNOWN_VARIABLE_NAMES.has(name)) found.add(m[1]);
  }
  return [...found];
}

// Pure render pipeline (the high-value testable unit). Order matches §4:
//   validateTemplate (stray-{{ escape ONLY when strayCount > 0, else raw body)
//   → merge (highlighted or plain) → fillBlankLines → DOMPurify (default config).
// No fetch, no send. renderCampaignTemplate/renderHighlightedTemplate leave unmapped
// {{tokens}} literally intact — what recipients would actually receive.
export function buildPreviewBody(
  bodyHtml: string,
  recipient: CampaignMergeRecipient,
  highlight = true,
): string {
  const sanitizedSource = validateTemplate(bodyHtml).sanitizedTemplate ?? bodyHtml;
  const mergedBody = highlight
    ? renderHighlightedTemplate(sanitizedSource, recipient)
    : renderCampaignTemplate(sanitizedSource, recipient);
  return DOMPurify.sanitize(fillBlankLines(mergedBody));
}

function TemplatePreviewPane({ subject, bodyHtml }: { subject: string; bodyHtml: string }) {
  // Pure client render — no fetch, no send. Highlighted merge for the visible body;
  // findUnmappedTokens reads the same stray-escaped source the merge ran on.
  const sanitizedSource = validateTemplate(bodyHtml).sanitizedTemplate ?? bodyHtml;
  const previewBody = buildPreviewBody(bodyHtml, PREVIEW_SAMPLE_RECIPIENT, true);
  const mergedSubject = renderCampaignTemplate(subject ?? '', PREVIEW_SAMPLE_RECIPIENT);
  const unmapped = findUnmappedTokens(sanitizedSource);

  return (
    <div className="p-3 space-y-3">
      <div className="text-xs text-muted-foreground">
        This is example data — the real contact's details (name, company, etc.) are filled in
        automatically when you send. Your sender signature and the unsubscribe / compliance
        footer are also added automatically at send time (shown as placeholders below).
      </div>

      {/* Soft safety warning for well-formed-but-unknown {{tokens}} that will send literally */}
      {unmapped.length > 0 && (
        <div className="text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-2 py-1">
          Heads up: {unmapped.map((t) => `{{${t}}}`).join(', ')}{' '}
          {unmapped.length === 1 ? "isn't a known merge field and will send as-is." : "aren't known merge fields and will send as-is."}
        </div>
      )}

      {/* Minimal email-header treatment: reinforces that sender/signature are bound elsewhere */}
      <div className="text-sm border-b pb-2 space-y-0.5">
        <div className="text-xs text-muted-foreground">From: your connected account</div>
        <div>
          <span className="text-muted-foreground">Subject: </span>
          <span className="font-medium">{mergedSubject || <em className="text-muted-foreground">(no subject)</em>}</span>
        </div>
      </div>

      {previewBody.trim() ? (
        <div
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: previewBody }}
        />
      ) : (
        <div className="text-sm text-muted-foreground italic">
          Nothing to preview yet — add some content in the Edit tab.
        </div>
      )}

      {/* Inline placeholder blocks at the BOTTOM of the body flow — where the missing content
          actually goes. Only shown when there's body content. */}
      {previewBody.trim() && (
        <div className="space-y-2 pt-1">
          <div className="border border-dashed rounded-md text-xs text-muted-foreground italic px-3 py-2">
            — Your signature appears here —
          </div>
          <div className="border border-dashed rounded-md text-xs text-muted-foreground italic px-3 py-2">
            — Unsubscribe link / mailing address appears here —
          </div>
        </div>
      )}
    </div>
  );
}

const templateSchema = z.object({
  name: z.string().min(1, 'Template name is required'),
  subject: z.string().min(1, 'Subject is required'),
  body: z.string().min(1, 'Template body is required'),
});

type FormValues = z.infer<typeof templateSchema>;

interface EmailTemplateEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: EmailTemplate | null;
  onSuccess?: () => void;
}

export function EmailTemplateEditor({
  open,
  onOpenChange,
  template,
  onSuccess,
}: EmailTemplateEditorProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [selectedTags, setSelectedTags] = useState<{ id: string; name: string; }[]>([]);
  // When non-null, the save was paused so the user can review variable-like
  // patterns that won't render at send time (unknown `{{var}}` names, or
  // `{var}` single-brace typos of known names). The user chooses to either
  // strip the braces and proceed, or cancel and fix manually.
  const [variableConfirm, setVariableConfirm] = useState<{
    values: FormValues;
    bodyToPersist: string;
    unknownDoubleBrace: string[];
    suspiciousSingleBrace: string[];
  } | null>(null);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  // Content captured (and stray-mustache-sanitized) at the moment the user opens
  // the test-send dialog. Non-null = dialog open.
  const [testContent, setTestContent] = useState<{ subject: string; body: string } | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [imageUploading, setImageUploading] = useState(false);
  // Saved editor selection captured at the moment the toolbar Image button
  // was clicked. The file picker steals focus from the editor and the async
  // upload pause-then-resume can lose the selection state — explicitly
  // restoring at insert time guarantees the image lands where the cursor
  // was when the user reached for the button. Paste / drop paths set
  // selection directly from the event position and don't use this ref.
  const savedSelectionRef = useRef<{ from: number; to: number } | null>(null);

  // Upload an image to the email-images bucket and insert it as <img src="https://...">
  // into the editor. Templates persist to the DB (and Hermes copies that body into
  // every per-recipient queue job), so we cannot use base64 here — see image-upload.ts.
  const handleImageInsert = async (file: File) => {
    if (!editor || !currentWorkspace) return;
    setImageUploading(true);
    try {
      const { url } = await uploadEmailImage(file, currentWorkspace.id);
      const chain = editor.chain().focus();
      if (savedSelectionRef.current) {
        chain.setTextSelection(savedSelectionRef.current);
        savedSelectionRef.current = null;
      }
      chain.setImage({ src: url, alt: file.name }).run();
      toast({ description: 'Image inserted at your cursor' });
    } catch (err) {
      const message =
        err instanceof ImageUploadError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to upload image';
      console.error('Error uploading template image:', err);
      captureError(err as Error, { feature: 'email-template-image-upload' });
      toast({
        title: 'Image upload failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setImageUploading(false);
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      createLinkExtension(),
      TiptapUnderline,
      // Click-to-resize image (Google-Docs-style corner/edge handles).
      // Images upload to the email-images Storage bucket and embed as
      // <img src> (allowBase64 stays at default `false` — pasted/dropped
      // data URLs would otherwise inflate template body and queue job
      // payloads). The renderHTML override emits a `width` HTML attribute +
      // inline `width: Npx` style so resized images render correctly in real
      // email clients. See image-resize-extension.ts.
      createImageExtension({
        inline: false,
        htmlClass: 'rounded-md max-w-full h-auto',
      }),
    ],
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[300px] p-3 border rounded-md',
      },
      ...linkEditorProps(),
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of Array.from(items)) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              handleImageInsert(file);
              return true;
            }
          }
        }
        return false;
      },
      handleDrop: (_view, event) => {
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;
        const imageFiles = Array.from(files).filter((f) =>
          f.type.startsWith('image/'),
        );
        if (imageFiles.length > 0) {
          event.preventDefault();
          imageFiles.forEach(handleImageInsert);
          return true;
        }
        return false;
      },
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: template?.name || '',
      subject: template?.subject || '',
      body: template?.body || '',
    },
  });

  useEffect(() => {
    if (editor) {
      editor.on('update', () => {
        const content = editor.getHTML();
        form.setValue('body', content);
      });
    }
  }, [editor, form]);

  useEffect(() => {
    // Reset to Edit on every (re)hydration so a fast close→re-open on a DIFFERENT
    // template never flashes the previous template's Preview for a frame (§5).
    setActiveTab('edit');
    if (template) {
      const fetchTemplateTags = async () => {
        try {
          const { data, error } = await supabase
            .from('template_tag_relations')
            .select(`
              tag_id,
              template_tags (
                id,
                name
              )
            `)
            .eq('template_id', template.id);

          if (error) throw error;

          const tags = data.map(relation => {
            const tag = Array.isArray(relation.template_tags) ? relation.template_tags[0] : relation.template_tags;
            return {
              id: tag?.id || '',
              name: tag?.name || ''
            };
          });

          setSelectedTags(tags);
        } catch (error) {
          console.error('Error fetching template tags:', error);
        }
      };

      fetchTemplateTags();
      form.reset({
        name: template.name,
        subject: template.subject,
        body: template.body,
      });
      editor?.commands.setContent(template.body);
    } else {
      form.reset({
        name: '',
        subject: '',
        body: '',
      });
      editor?.commands.setContent('');
      setSelectedTags([]);
    }
  }, [template, form, editor]);

  const createTemplateVariables = (variableNames: string[]): TemplateVariable[] => {
    return variableNames.map(name => ({
      name,
      type: 'string',
      required: true,
    }));
  };

  const handleImageButtonClick = () => {
    // Capture the editor selection right before the file picker opens so we
    // can restore it on insert. The native file dialog steals focus and the
    // async upload that follows can leave the editor's stored selection
    // stale; this ref is the source of truth for placement.
    if (editor) {
      const { from, to } = editor.state.selection;
      savedSelectionRef.current = { from, to };
    }
    imageInputRef.current?.click();
  };

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach(handleImageInsert);
    }
    // Reset so the same file can be selected again
    event.target.value = '';
  };

  // Strip the braces around variable-like patterns the user confirmed are
  // not merge tags. For unknown `{{var}}`, remove the surrounding `{{ }}` so
  // the email renders the bare word. For suspicious `{var}` single-braces,
  // do the same. Both fixes are applied as text replacements scoped to the
  // exact names the validator flagged (case-insensitive).
  const stripUnknownBraces = (
    body: string,
    unknown: string[],
    suspicious: string[],
  ): string => {
    let out = body;
    const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    for (const name of unknown) {
      const re = new RegExp(`\\{\\{\\s*${escapeRe(name)}\\s*\\}\\}`, 'gi');
      out = out.replace(re, name);
    }
    for (const name of suspicious) {
      const re = new RegExp(
        `(?<!\\{)\\{\\s*${escapeRe(name)}\\s*\\}(?!\\})`,
        'gi',
      );
      out = out.replace(re, name);
    }
    return out;
  };

  // The actual DB-writing path. Split out of onSubmit so we can call it
  // either directly (no variable issues) or after the user confirms the
  // "strip braces and save" path in the AlertDialog.
  const persistTemplate = async (values: FormValues, bodyToPersist: string) => {
    if (!currentWorkspace) return;
    setIsLoading(true);
    try {
      const variableNames = extractVariables(bodyToPersist);
      const variables = createTemplateVariables(variableNames);

      if (template?.id) {
        // Update existing template
        const { error } = await supabase
          .from('email_templates')
          .update({
            name: values.name,
            subject: values.subject,
            body: bodyToPersist,
            variables,
            updated_at: new Date().toISOString(),
          })
          .eq('id', template.id);

        if (error) throw error;

        // Update tags
        await supabase
          .from('template_tag_relations')
          .delete()
          .eq('template_id', template.id);

        if (selectedTags.length > 0) {
          const tagRelations = selectedTags.map(tag => ({
            template_id: template.id,
            tag_id: tag.id
          }));

          const { error: tagError } = await supabase
            .from('template_tag_relations')
            .insert(tagRelations);

          if (tagError) throw tagError;
        }

        toast({
          description: "Template updated successfully",
        });
      } else {
        // Create new template
        const { data, error } = await supabase
          .from('email_templates')
          .insert({
            name: values.name,
            subject: values.subject,
            body: bodyToPersist,
            variables,
            workspace_id: currentWorkspace.id,
          })
          .select();

        if (error) throw error;

        // Add tags for new template
        if (selectedTags.length > 0 && data?.[0]?.id) {
          const tagRelations = selectedTags.map(tag => ({
            template_id: data[0].id,
            tag_id: tag.id
          }));

          const { error: tagError } = await supabase
            .from('template_tag_relations')
            .insert(tagRelations);

          if (tagError) throw tagError;
        }

        toast({
          description: "Template created successfully",
        });
      }

      onSuccess?.();
      onOpenChange(false);
      setVariableConfirm(null);
    } catch (error) {
      console.error('Error saving template:', error);
      captureError(error as Error, { feature: 'email-template-save' });
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save template",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    if (!currentWorkspace || !editor) return;

    const editorContent = editor.getHTML();
    const {
      isValid,
      error: validationError,
      sanitizedTemplate,
      strayCount,
      unknownDoubleBrace,
      suspiciousSingleBrace,
    } = validateTemplate(editorContent);

    if (!isValid) {
      // Surface a friendlier wrapper around the raw Handlebars compiler error
      // so non-technical users get something readable. Preserve the original
      // message for support / debugging.
      toast({
        title: 'Error',
        description: validationError
          ? `Template has invalid merge tag syntax. Original error: ${validationError}`
          : 'Template has invalid merge tag syntax.',
        variant: 'destructive',
      });
      return;
    }

    // If the validator cleaned up stray `{{` that looked like broken paste,
    // persist the sanitized version (otherwise the bad braces would re-throw
    // at send time, after the user has already saved) and surface a
    // non-destructive toast so they know to review what we cleaned up.
    const bodyAfterStrays = sanitizedTemplate ?? editorContent;
    if (sanitizedTemplate && strayCount && strayCount > 0) {
      toast({
        description: `Cleaned up ${strayCount} stray merge tag${
          strayCount === 1 ? '' : 's'
        } that looked like broken paste — review the saved template and re-add merge variables if needed.`,
      });
    }

    // Detect variable-like patterns the backend won't substitute (unknown
    // `{{var}}` names + `{var}` single-brace typos). If any exist, pause the
    // save and let the user pick between "remove the braces" (strip them and
    // save) or "let me fix it" (close dialog without saving).
    const hasUnknown = (unknownDoubleBrace?.length ?? 0) > 0;
    const hasSuspicious = (suspiciousSingleBrace?.length ?? 0) > 0;
    if (hasUnknown || hasSuspicious) {
      setVariableConfirm({
        values,
        bodyToPersist: bodyAfterStrays,
        unknownDoubleBrace: unknownDoubleBrace ?? [],
        suspiciousSingleBrace: suspiciousSingleBrace ?? [],
      });
      return;
    }

    await persistTemplate(values, bodyAfterStrays);
  };

  const handleStripBracesAndSave = async () => {
    if (!variableConfirm) return;
    const stripped = stripUnknownBraces(
      variableConfirm.bodyToPersist,
      variableConfirm.unknownDoubleBrace,
      variableConfirm.suspiciousSingleBrace,
    );
    await persistTemplate(variableConfirm.values, stripped);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {template ? 'Edit Template' : 'Create Template'}
          </DialogTitle>
          <DialogDescription>
            Create or edit an email template using Handlebars syntax.
            Use &#123;&#123;variable&#125;&#125; to define template variables.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} onKeyDown={preventEnterSubmit} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter template name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter email subject" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="body"
              render={() => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'edit' | 'preview')}>
                      <TabsList className="h-8">
                        <TabsTrigger value="edit" className="text-xs px-3">Edit</TabsTrigger>
                        <TabsTrigger value="preview" className="text-xs px-3">Preview</TabsTrigger>
                      </TabsList>
                    </Tabs>

                    {activeTab === 'edit' && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => editor?.chain().focus().toggleBold().run()}
                      >
                        <Bold className="h-4 w-4" />
                      </Button>
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm" 
                        onClick={() => editor?.chain().focus().toggleItalic().run()}
                      >
                        <Italic className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => editor?.chain().focus().toggleUnderline().run()}
                        className={cn(editor?.isActive('underline') && 'bg-muted')}
                      >
                        <Underline className="h-4 w-4" />
                      </Button>
                      <LinkPopover editor={editor} />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleImageButtonClick}
                        disabled={imageUploading || !currentWorkspace}
                        aria-label="Insert image"
                        title={imageUploading ? 'Uploading…' : 'Insert image'}
                      >
                        <ImageIcon className="h-4 w-4" />
                      </Button>
                      <input
                        type="file"
                        ref={imageInputRef}
                        className="hidden"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        multiple
                        onChange={handleImageFileChange}
                      />
                      <VariableSelector
                        onSelect={(variable) => {
                          editor?.commands.insertContent(variable);
                          editor?.commands.focus();
                        }}
                      />
                    </div>
                    )}
                  </div>
                  <FormControl>
                    {/* Internal scroll so pasting a long email body doesn't
                        push the modal off-screen. The inner prose has its own
                        min-h-[300px] floor; the wrapper's min-h-[200px] is the
                        floor for the wrapper box itself. */}
                    <div className="border rounded-md max-h-[50vh] min-h-[200px] overflow-y-auto">
                      {/* Editor stays MOUNTED; hidden (not unmounted) in Preview so TipTap doc,
                          selection, and undo/redo history survive tab round-trips. getHTML() reads
                          the in-memory ProseMirror doc, so visibility doesn't affect it. */}
                      <div className={cn(activeTab === 'preview' && 'hidden')}>
                        <EditorContent editor={editor} />
                      </div>

                      {activeTab === 'preview' && (
                        <TemplatePreviewPane
                          subject={form.getValues('subject')}
                          bodyHtml={editor?.getHTML() ?? form.getValues('body') ?? ''}
                        />
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              name="tags"
              render={() => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Tags</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Type to search or create new tags
                    </div>
                  </div>
                  <FormControl>
                    <TagInput
                      selectedTags={selectedTags}
                      onTagsChange={setSelectedTags}
                    />
                  </FormControl>
                  <p className="text-sm text-muted-foreground mt-1">
                    Selected tags will help organize and filter templates
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const html = editor?.getHTML() ?? '';
                  // Reuse the on-save sanitize so stray `{var}`/`{{` don't go out
                  // in the test (mirrors onSubmit). renderCampaignTemplate leaves
                  // unknown tokens untouched, so this is the only cleanup it gets.
                  const { sanitizedTemplate } = validateTemplate(html);
                  setTestContent({
                    subject: form.getValues('subject') || '',
                    body: sanitizedTemplate ?? html,
                  });
                }}
                disabled={!form.watch('subject')?.trim()}
              >
                Send Test Email
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : (template ? "Update Template" : "Create Template")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      {currentWorkspace && (
        <SendTestEmailDialog
          open={!!testContent}
          onOpenChange={(o) => { if (!o) setTestContent(null); }}
          subject={testContent?.subject ?? ''}
          body={testContent?.body ?? ''}
          workspaceId={currentWorkspace.id}
          showSenderPicker
        />
      )}

      <AlertDialog
        open={!!variableConfirm}
        onOpenChange={(o) => {
          if (!o && !isLoading) setVariableConfirm(null);
        }}
      >
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Did you mean to add merge variables?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  The following patterns look like merge tags but won't be
                  filled in at send time. Recipients will see them literally.
                </p>
                {variableConfirm && variableConfirm.unknownDoubleBrace.length > 0 && (
                  <div className="rounded-md border bg-muted/50 p-3 text-sm">
                    <div className="mb-1 font-medium text-foreground">
                      Unknown variable name{variableConfirm.unknownDoubleBrace.length === 1 ? '' : 's'}:
                    </div>
                    <ul className="ml-4 list-disc space-y-0.5 font-mono text-xs">
                      {variableConfirm.unknownDoubleBrace.map((v) => (
                        <li key={`unknown-${v}`}>{`{{${v}}}`}</li>
                      ))}
                    </ul>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Our system supports: <code>first_name</code>, <code>last_name</code>,
                      <code> company</code>, <code>email</code>, <code>occupation</code>,
                      <code> phone</code>.
                    </p>
                  </div>
                )}
                {variableConfirm && variableConfirm.suspiciousSingleBrace.length > 0 && (
                  <div className="rounded-md border bg-muted/50 p-3 text-sm">
                    <div className="mb-1 font-medium text-foreground">
                      Single-brace typo{variableConfirm.suspiciousSingleBrace.length === 1 ? '' : 's'} (merge tags need double braces):
                    </div>
                    <ul className="ml-4 list-disc space-y-0.5 font-mono text-xs">
                      {variableConfirm.suspiciousSingleBrace.map((v) => (
                        <li key={`single-${v}`}>
                          {`{${v}}`} <span className="text-muted-foreground">→ should be</span> {`{{${v}}}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-sm">
                  Choose <strong>Let me fix them</strong> to close this dialog
                  and edit the template, or <strong>Remove the braces</strong>{' '}
                  to strip the braces and save the bare names as plain text.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>
              Let me fix them
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStripBracesAndSave}
              disabled={isLoading}
            >
              Remove the braces and save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}